import React, { useEffect, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Form,
  Input,
  List,
  Popconfirm,
  Row,
  Skeleton,
  Space,
  Switch,
  Tabs,
  Tag,
  Typography
} from "antd";
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  EuroOutlined,
  FileSearchOutlined,
  InboxOutlined,
  LinkOutlined,
  PlusOutlined,
  UploadOutlined
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import {
  deleteDocument,
  deleteOrder,
  deleteSubcontractor,
  documentFileUrl,
  getMeta,
  getSubcontractor,
  revokeUploadLink,
  updateDocument,
  updateSubcontractor
} from "./api";
import ComplianceTag from "./ComplianceTag";
import { RequestDocumentsModal, ReviewDocumentModal, UploadDocumentModal } from "./DocumentDialogs";
import { OrderModal, PaymentModal, WarningList } from "./OrderDialogs";
import {
  STATUS_COLORS,
  formatDateTime,
  formatDay,
  formatEuro,
  orderStatusLabels,
  statusLabels,
  typeLabel
} from "./format";
import "./subcontractors.css";

const DocumentsTab = ({ sub, meta, onChange, onUpload, onReview }) => {
  const { t } = useTranslation("dashboard");
  const [showArchived, setShowArchived] = useState(false);
  const labels = statusLabels(t);

  const act = async (doc, action) => {
    try {
      const res =
        action === "delete"
          ? await deleteDocument(sub._id, doc._id)
          : await updateDocument(sub._id, doc._id, { action });
      onChange(res.data.data);
    } catch (error) {
      generalErrorAlert(error);
    }
  };

  return (
    <>
      <List
        className="sc-types"
        dataSource={sub.compliance.types}
        renderItem={type => {
          const docs = sub.documents.filter(
            d => d.type === type.type && (showArchived || !d.archived)
          );
          return (
            <List.Item key={type.type}>
              <div className="sc-type">
                <div className="sc-type-head">
                  <Typography.Text strong className="sc-type-title">
                    {typeLabel(meta?.documentTypes, type.type)}
                  </Typography.Text>
                  <Space size={[4, 4]} wrap>
                    {!type.required && <Tag>{t("Optional")}</Tag>}
                    <Tag color={STATUS_COLORS[type.status]}>{labels[type.status]}</Tag>
                    {type.until && (
                      <span className="sc-small">
                        {t("until {{date}}", { date: formatDay(type.until) })}
                      </span>
                    )}
                  </Space>
                  {meta?.can.create && (
                    <Button
                      size="small"
                      icon={<UploadOutlined />}
                      onClick={() => onUpload(type.type)}
                    >
                      {type.status === "missing" ? t("Add") : t("Renew")}
                    </Button>
                  )}
                </div>
                {docs.map(doc => (
                  <div key={doc._id} className={`sc-doc${doc.archived ? " sc-doc-archived" : ""}`}>
                    <div className="sc-doc-main">
                      <Tag color={STATUS_COLORS[doc.status]}>{labels[doc.status]}</Tag>
                      <span className="sc-break">
                        {[doc.issuer, doc.number].filter(Boolean).join(" · ") ||
                          doc.fileName ||
                          "—"}
                      </span>
                      <span className="sc-small">
                        {doc.effectiveUntil
                          ? t("until {{date}}", { date: formatDay(doc.effectiveUntil) })
                          : ""}
                        {doc.source === "LINK" ? ` · ${t("uploaded by the subcontractor")}` : ""}
                        {doc.archived ? ` · ${t("archived")}` : ""}
                      </span>
                    </div>
                    <Space size={4} wrap>
                      {doc.hasFile && (
                        <Button
                          size="small"
                          href={documentFileUrl(sub._id, doc._id)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {t("Scan")}
                        </Button>
                      )}
                      <Button
                        size="small"
                        icon={<FileSearchOutlined />}
                        onClick={() => onReview(doc._id)}
                      >
                        {doc.state === "pending_review" ? t("Review") : t("Details")}
                      </Button>
                      {meta?.can.update && doc.state === "confirmed" && !doc.archived && (
                        <Popconfirm
                          title={t("Archive this document?")}
                          onConfirm={() => act(doc, "archive")}
                        >
                          <Button size="small" icon={<InboxOutlined />} aria-label={t("Archive")} />
                        </Popconfirm>
                      )}
                      {meta?.can.update && doc.archived && (
                        <Button size="small" onClick={() => act(doc, "restore")}>
                          {t("Restore")}
                        </Button>
                      )}
                      {meta?.can.delete && doc.state !== "confirmed" && (
                        <Popconfirm
                          title={t("Delete this upload?")}
                          onConfirm={() => act(doc, "delete")}
                        >
                          <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            aria-label={t("Delete")}
                          />
                        </Popconfirm>
                      )}
                    </Space>
                  </div>
                ))}
              </div>
            </List.Item>
          );
        }}
      />
      {sub.documents.some(d => d.archived) && (
        <Space className="mt-3">
          <Switch size="small" checked={showArchived} onChange={setShowArchived} />
          {t("Show archived documents")}
        </Space>
      )}
    </>
  );
};

const OrdersTab = ({ sub, meta, onEdit, onPay, onChange }) => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const statuses = orderStatusLabels(t);
  const remove = async order => {
    try {
      await deleteOrder(order._id);
      onChange();
    } catch (error) {
      generalErrorAlert(error);
    }
  };
  const columns = [
    {
      title: t("Order"),
      key: "title",
      mobile: "title",
      render: (_, o) => (
        <div>
          <Typography.Text strong>{o.number}</Typography.Text> {o.title}
          <div className="sc-small">{o.projectName}</div>
        </div>
      )
    },
    {
      title: t("Status"),
      key: "status",
      mobile: "badge",
      render: (_, o) => (
        <Space size={[4, 4]} wrap>
          <Tag>{statuses[o.status]}</Tag>
          {o.assignmentCheck?.acknowledged && (
            <Tag color="warning">{t("Assigned despite warnings")}</Tag>
          )}
        </Space>
      )
    },
    {
      title: t("Price"),
      key: "price",
      align: "right",
      render: (_, o) => formatEuro(o.price, lng)
    },
    {
      title: t("Paid"),
      key: "paid",
      align: "right",
      render: (_, o) => formatEuro(o.totals.paid, lng)
    },
    {
      title: t("Withheld"),
      key: "withheld",
      align: "right",
      render: (_, o) => formatEuro(o.totals.withheld + o.totals.retained, lng)
    },
    {
      title: "",
      key: "actions",
      mobile: "actions",
      render: (_, o) => (
        <Space size={4} wrap>
          {meta?.can.update && o.status !== "cancelled" && (
            <Button size="small" icon={<EuroOutlined />} onClick={() => onPay(o)}>
              {t("Payment")}
            </Button>
          )}
          {meta?.can.update && (
            <Button size="small" onClick={() => onEdit(o)}>
              {t("Edit")}
            </Button>
          )}
          {meta?.can.delete && !o.payments.length && (
            <Popconfirm title={t("Delete this order?")} onConfirm={() => remove(o)}>
              <Button size="small" danger icon={<DeleteOutlined />} aria-label={t("Delete")} />
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];
  return (
    <>
      {meta?.can.create && (
        <Button
          className="mb-3"
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => onEdit(null)}
        >
          {t("New order")}
        </Button>
      )}
      {sub.orders.length ? (
        <ResponsiveTable
          rowKey="_id"
          columns={columns}
          dataSource={sub.orders}
          mobileAvatar={false}
          pagination={false}
          scroll={{ x: 720 }}
          expandable={{
            rowExpandable: o => o.payments.length > 0,
            expandedRowRender: o => (
              <List
                size="small"
                dataSource={o.payments}
                renderItem={p => (
                  <List.Item key={p._id}>
                    <span>
                      {formatDay(p.date)} {p.invoiceNumber}
                    </span>
                    <span className="sc-num">
                      {formatEuro(p.gross, lng)} → {formatEuro(p.payout, lng)}
                      {p.withheld ? ` (${t("Bauabzugsteuer")} ${formatEuro(p.withheld, lng)})` : ""}
                    </span>
                  </List.Item>
                )}
              />
            )
          }}
        />
      ) : (
        <Empty description={t("No orders yet")} />
      )}
    </>
  );
};

const DetailsTab = ({ sub, meta, onChange }) => {
  const { t } = useTranslation("dashboard");
  const history = useHistory();
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    form.setFieldsValue(sub);
  }, [sub._id, sub.updatedAt]);

  const save = async values => {
    setBusy(true);
    try {
      const res = await updateSubcontractor(sub._id, values);
      Success(t("Saved"));
      onChange(res.data.data);
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    try {
      await deleteSubcontractor(sub._id);
      history.push("/user/subcontractors");
    } catch (error) {
      generalErrorAlert(error);
    }
  };

  const supplier = sub.supplier || {};
  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} lg={14}>
        <Form form={form} layout="vertical" onFinish={save} disabled={!meta?.can.update}>
          <Row gutter={[12, 0]}>
            <Col xs={24} md={12}>
              <Form.Item name="trade" label={t("Trade")}>
                <Input maxLength={200} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="contactPerson" label={t("Contact person")}>
                <Input maxLength={200} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="email"
                label={t("Email for document requests")}
                rules={[{ type: "email" }]}
              >
                <Input maxLength={200} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="phone" label={t("Phone")}>
                <Input maxLength={100} />
              </Form.Item>
            </Col>
            {[
              ["bauleistungen", t("Performs construction work")],
              ["kleinunternehmer", t("Kleinunternehmer (no VAT)")],
              [["requirements", "hasEmployees"], t("Has employees")],
              [["requirements", "sokaBau"], t("Main construction trade (SOKA-BAU)")],
              [["requirements", "foreignWorkers"], t("Workers posted from abroad (A1)")],
              ["autoRequest", t("Ask for renewed documents automatically")],
              ["active", t("Active")]
            ].map(([name, label]) => (
              <Col xs={24} md={12} key={String(name)}>
                <Form.Item name={name} label={label} valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Col>
            ))}
            <Col xs={24}>
              <Form.Item name="notes" label={t("Notes")}>
                <Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} maxLength={5000} />
              </Form.Item>
            </Col>
          </Row>
          {meta?.can.update && (
            <Button type="primary" htmlType="submit" loading={busy}>
              {t("Save")}
            </Button>
          )}
        </Form>
      </Col>
      <Col xs={24} lg={10}>
        <Card size="small" title={t("Supplier (accounting)")}>
          <Descriptions size="small" column={1}>
            <Descriptions.Item label={t("Supplier number")}>
              {supplier.supplierNumber}
            </Descriptions.Item>
            <Descriptions.Item label={t("Tax number")}>
              {supplier.taxNumber || "—"}
            </Descriptions.Item>
            <Descriptions.Item label={t("VAT ID")}>{supplier.vatId || "—"}</Descriptions.Item>
            <Descriptions.Item label="IBAN">{supplier.ibans?.[0]?.iban || "—"}</Descriptions.Item>
          </Descriptions>
          <Typography.Paragraph type="secondary" className="sc-small">
            {t(
              "Name, address and bank details are kept in the accounting supplier. A confirmed Freistellungsbescheinigung is written there automatically."
            )}
          </Typography.Paragraph>
          <Link to="/user/accounting/suppliers">{t("Open suppliers")}</Link>
        </Card>
        {meta?.can.delete && !sub.orders.length && (
          <Popconfirm
            title={t("Delete this subcontractor? The supplier stays.")}
            onConfirm={remove}
          >
            <Button className="mt-3" danger icon={<DeleteOutlined />}>
              {t("Delete")}
            </Button>
          </Popconfirm>
        )}
      </Col>
    </Row>
  );
};

const LogTab = ({ sub, meta, onChange }) => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const contexts = {
    assignment: t("Assignment to a project"),
    payment_release: t("Payment release"),
    payment_batch: t("Payment run"),
    beleg_check: t("Invoice check")
  };
  const revoke = async link => {
    try {
      const res = await revokeUploadLink(sub._id, link._id);
      onChange(res.data.data);
    } catch (error) {
      generalErrorAlert(error);
    }
  };
  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} lg={14}>
        <Card size="small" title={t("Compliance checks")}>
          <List
            size="small"
            locale={{ emptyText: t("No checks yet") }}
            dataSource={sub.complianceLog}
            renderItem={(entry, index) => (
              <List.Item key={index}>
                <div className="sc-full">
                  <Space size={[4, 4]} wrap>
                    <ComplianceTag compliance={{ level: entry.level }} />
                    <Typography.Text strong>{contexts[entry.context]}</Typography.Text>
                    <span className="sc-small">
                      {formatDateTime(entry.at, lng)} {entry.note}
                    </span>
                    {entry.acknowledged && (
                      <Tag color="warning">{t("Continued despite warnings")}</Tag>
                    )}
                    {entry.withheld > 0 && (
                      <Tag>
                        {t("Withheld {{amount}}", { amount: formatEuro(entry.withheld, lng) })}
                      </Tag>
                    )}
                  </Space>
                  {entry.warnings.length > 0 && <WarningList warnings={entry.warnings} />}
                </div>
              </List.Item>
            )}
          />
        </Card>
      </Col>
      <Col xs={24} lg={10}>
        <Card size="small" title={t("Upload links")} className="mb-3">
          <List
            size="small"
            locale={{ emptyText: t("No links") }}
            dataSource={sub.uploadLinks}
            renderItem={link => (
              <List.Item
                key={link._id}
                actions={
                  link.active && meta?.can.update
                    ? [
                        <Button key="revoke" size="small" onClick={() => revoke(link)}>
                          {t("Revoke")}
                        </Button>
                      ]
                    : []
                }
              >
                <div>
                  <Tag color={link.active ? "processing" : "default"}>
                    {link.active ? t("Active") : t("Expired")}
                  </Tag>
                  <span className="sc-small">
                    {t("until {{date}}", { date: formatDateTime(link.expiresAt, lng) })}
                    {link.sentTo ? ` · ${link.sentTo}` : ""}
                    {link.automatic ? ` · ${t("automatic")}` : ""}
                    {` · ${t("{{n}} uploads", { n: link.uploads })}`}
                  </span>
                </div>
              </List.Item>
            )}
          />
        </Card>
        <Card size="small" title={t("Expiry reminders")}>
          <List
            size="small"
            locale={{ emptyText: t("No reminders yet") }}
            dataSource={sub.reminders}
            renderItem={r => (
              <List.Item key={r.key}>
                <span className="sc-small">
                  {formatDateTime(r.sentAt, lng)} · {typeLabel(meta?.documentTypes, r.type)} ·{" "}
                  {r.stage ? t("{{n}} days before", { n: r.stage }) : t("expiry day")}
                </span>
              </List.Item>
            )}
          />
        </Card>
      </Col>
    </Row>
  );
};

const SubcontractorDetail = () => {
  const { t } = useTranslation("dashboard");
  const { id } = useParams();
  const [meta, setMeta] = useState(null);
  const [sub, setSub] = useState(null);
  const [tab, setTab] = useState("documents");
  const [upload, setUpload] = useState({ open: false, type: undefined });
  const [reviewId, setReviewId] = useState(null);
  const [requestOpen, setRequestOpen] = useState(false);
  const [orderModal, setOrderModal] = useState({ open: false, order: null });
  const [payOrder, setPayOrder] = useState(null);

  const load = () =>
    getSubcontractor(id)
      .then(res => setSub(res.data.data))
      .catch(generalErrorAlert);

  useEffect(() => {
    getMeta()
      .then(res => setMeta(res.data))
      .catch(generalErrorAlert);
    load();
  }, [id]);

  if (!sub)
    return (
      <div className="subcontractors-page">
        <Card>
          <Skeleton active paragraph={{ rows: 8 }} />
        </Card>
      </div>
    );

  const c = sub.compliance;
  const reviewDoc = sub.documents.find(d => d._id === reviewId);
  const pending = sub.documents.filter(d => d.state === "pending_review" && !d.archived);

  return (
    <div className="subcontractors-page">
      <div className="sc-header mb-3">
        <Link to="/user/subcontractors" aria-label={t("Back")}>
          <ArrowLeftOutlined />
        </Link>
        <div className="sc-header-title">
          <Typography.Title level={4}>{sub.name}</Typography.Title>
          <ComplianceTag compliance={c} />
        </div>
        <Space wrap>
          {meta?.can.update && (
            <Button icon={<LinkOutlined />} onClick={() => setRequestOpen(true)}>
              {t("Request documents")}
            </Button>
          )}
          {meta?.can.create && (
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={() => setUpload({ open: true })}
            >
              {t("Add document")}
            </Button>
          )}
        </Space>
      </div>

      {sub.bauleistungen && !c.freistellung.valid && (
        <Alert
          className="mb-3"
          type="error"
          showIcon
          message={t("No valid Freistellungsbescheinigung")}
          description={t(
            "15 % of every payment must be withheld and paid to the Finanzamt (§ 48 EStG), unless this year's payments stay within 5,000 €."
          )}
        />
      )}
      {sub.bauleistungen && c.freistellung.valid && (
        <Alert
          className="mb-3"
          type="success"
          showIcon
          message={t("Freistellungsbescheinigung {{number}} valid until {{date}}", {
            number: c.freistellung.number,
            date: formatDay(c.freistellung.validUntil)
          })}
        />
      )}
      {sub.reverseCharge.applies && (
        <Alert
          className="mb-3"
          type="info"
          showIcon
          message={t(
            "§ 13b UStG: invoices for construction work come without VAT; your company owes the tax (reverse charge)."
          )}
        />
      )}
      {pending.length > 0 && (
        <Alert
          className="mb-3"
          type="warning"
          showIcon
          message={t("{{n}} documents wait for your review", { n: pending.length })}
          action={
            <Button size="small" onClick={() => setReviewId(pending[0]._id)}>
              {t("Review")}
            </Button>
          }
        />
      )}

      <Card>
        <Tabs
          activeKey={tab}
          onChange={setTab}
          items={[
            {
              key: "documents",
              label: t("Documents"),
              children: (
                <DocumentsTab
                  sub={sub}
                  meta={meta}
                  onChange={setSub}
                  onUpload={type => setUpload({ open: true, type })}
                  onReview={setReviewId}
                />
              )
            },
            {
              key: "orders",
              label: `${t("Orders")} (${sub.orders.length})`,
              children: (
                <OrdersTab
                  sub={sub}
                  meta={meta}
                  onEdit={order => setOrderModal({ open: true, order })}
                  onPay={setPayOrder}
                  onChange={load}
                />
              )
            },
            {
              key: "details",
              label: t("Details"),
              children: <DetailsTab sub={sub} meta={meta} onChange={setSub} />
            },
            {
              key: "log",
              label: t("History"),
              children: <LogTab sub={sub} meta={meta} onChange={setSub} />
            }
          ]}
        />
      </Card>

      <UploadDocumentModal
        open={upload.open}
        sub={sub}
        meta={meta}
        presetType={upload.type}
        onClose={() => setUpload({ open: false })}
        onSaved={(data, documentId) => {
          setSub(data);
          setUpload({ open: false });
          const added = data.documents.find(d => d._id === documentId);
          if (added && added.state === "pending_review") setReviewId(documentId);
        }}
      />
      <ReviewDocumentModal
        open={Boolean(reviewDoc)}
        sub={sub}
        doc={reviewDoc}
        meta={meta}
        fileUrl={reviewDoc ? documentFileUrl(sub._id, reviewDoc._id) : ""}
        onClose={() => setReviewId(null)}
        onSaved={(data, keepOpen) => {
          setSub(data);
          if (!keepOpen) setReviewId(null);
        }}
      />
      <RequestDocumentsModal
        open={requestOpen}
        sub={sub}
        meta={meta}
        onClose={() => setRequestOpen(false)}
        onSaved={setSub}
      />
      <OrderModal
        open={orderModal.open}
        sub={sub}
        meta={meta}
        order={orderModal.order}
        onClose={() => setOrderModal({ open: false, order: null })}
        onSaved={() => {
          setOrderModal({ open: false, order: null });
          load();
        }}
      />
      <PaymentModal
        open={Boolean(payOrder)}
        order={payOrder}
        onClose={() => setPayOrder(null)}
        onSaved={() => {
          setPayOrder(null);
          load();
        }}
      />
    </div>
  );
};

export default SubcontractorDetail;
